import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import {
  ArrowLeft, User, Building2, MapPin, Briefcase, Users, Clock,
  TrendingUp, TrendingDown, DollarSign, Calendar, BarChart3
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, BarChart, Cell
} from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const METRIC_COLORS = {
  premium: '#FF8C42',
  non_premium: '#4CAF93',
  total: '#2E5C8A',
  current: '#ef4444'
};

const EmployeeDeepDive = () => {
  const { employeeId } = useParams();
  const { API } = useContext(AppContext);
  const { filters } = useFilters();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/employees/deep-dive`, {
          employee_id: employeeId,
          start_date: filters?.start_date || undefined,
          end_date: filters?.end_date || undefined
        });
        setData(response.data);
      } catch (err) {
        toast.error('Failed to load employee data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API, employeeId, filters?.start_date, filters?.end_date]);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto animate-pulse space-y-6">
        <div className="h-8 w-48 bg-slate-200 rounded"></div>
        <div className="h-48 bg-slate-100 rounded-xl"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-64 bg-slate-100 rounded-xl"></div>
          <div className="h-64 bg-slate-100 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!data?.employee?.name) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-center py-20">
        <User className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-600">Employee Not Found</h2>
        <p className="text-slate-500 mt-1">No data found for this employee in the selected date range.</p>
        <Button onClick={() => navigate(-1)} className="mt-4" variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const emp = data.employee;
  const trend = data.monthly_trend || [];
  const dow = data.day_of_week || [];
  const payTypes = data.pay_type_breakdown || [];
  const peers = data.peers || [];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="employee-deep-dive">
      {/* Back + Header */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#2E5C8A] mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 p-6 mb-6"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#2E5C8A]/10 flex items-center justify-center">
              <User className="h-7 w-7 text-[#2E5C8A]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1e3a5f]">{emp.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {emp.job_title}</span>
                <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {emp.department}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {emp.location}</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {emp.supervisor}</span>
              </div>
            </div>
          </div>
          <div className="text-right text-sm text-slate-400">
            ID: {emp.employee_id} &middot; ${emp.hourly_rate?.toFixed(2)}/hr
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500">Total Hours</p>
            <p className="text-xl font-bold text-[#1e3a5f]">{emp.total_hours?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500">Premium Hours</p>
            <p className="text-xl font-bold text-[#FF8C42]">{emp.premium_hours?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500">Non-Premium</p>
            <p className="text-xl font-bold text-[#4CAF93]">{emp.non_premium_hours?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500">Premium %</p>
            <p className="text-xl font-bold text-red-500">{emp.premium_percent}%</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500">Days Worked</p>
            <p className="text-xl font-bold text-[#2E5C8A]">{emp.days_worked}</p>
          </div>
        </div>
      </motion.div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#2E5C8A]" /> Monthly Overtime Trend
          </h3>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={v => v?.substring(0, 7)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => v.toLocaleString()} labelFormatter={v => v?.substring(0, 7)} />
                <Legend />
                <Bar dataKey="premium_hours" name="Premium" fill={METRIC_COLORS.premium} radius={[3, 3, 0, 0]} />
                <Line dataKey="premium_percent" name="Premium %" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} yAxisId="right" />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-center py-8">No trend data</p>}
        </motion.div>

        {/* Day of Week */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#2E5C8A]" /> Hours by Day of Week
          </h3>
          {dow.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={dow}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={v => v?.substring(0, 3)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="premium_hours" name="Premium" fill={METRIC_COLORS.premium} radius={[3, 3, 0, 0]} />
                <Bar dataKey="non_premium_hours" name="Non-Premium" fill={METRIC_COLORS.non_premium} radius={[3, 3, 0, 0]} opacity={0.7} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-center py-8">No day-of-week data</p>}
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pay Type Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#FF8C42]" /> Pay Type Breakdown
          </h3>
          <div className="space-y-2">
            {payTypes.map(pt => (
              <div key={pt.pay_type} className="flex items-center gap-3 py-2">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${pt.is_premium ? 'bg-[#FF8C42]' : 'bg-[#4CAF93]'}`} />
                <span className="text-sm text-slate-700 flex-1">{pt.pay_type}</span>
                <span className="text-sm font-medium text-slate-800">{pt.hours?.toLocaleString()} hrs</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${pt.is_premium ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                  {pt.is_premium ? 'Premium' : 'Regular'}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Peer Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-500" /> Peer Comparison
          </h3>
          <p className="text-xs text-slate-400 mb-4">Same job title & location</p>
          <div className="space-y-2">
            {peers.map((peer, i) => (
              <div
                key={peer.employee_id}
                className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${peer.is_current ? 'bg-[#2E5C8A]/5 border border-[#2E5C8A]/20' : 'hover:bg-slate-50'}`}
              >
                <span className="text-xs text-slate-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  {peer.is_current ? (
                    <p className="text-sm font-bold text-[#2E5C8A] truncate">
                      {peer.name} (This employee)
                    </p>
                  ) : (
                    <button
                      onClick={() => navigate(`/employee/${peer.employee_id}`)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate transition-colors cursor-pointer"
                      data-testid={`peer-deep-link-${peer.employee_id}`}
                    >
                      {peer.name}
                    </button>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium text-[#FF8C42]">{peer.premium_hours?.toLocaleString()} premium</p>
                  <p className="text-xs text-slate-400">{peer.premium_percent}%</p>
                </div>
              </div>
            ))}
            {peers.length === 0 && <p className="text-slate-400 text-center py-4">No peers found</p>}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EmployeeDeepDive;
